import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Cookie } from "@phosphor-icons/react";

const STORAGE_KEY = "ft_cookie_consent_v1";

// Reads current consent from localStorage. Returns null if not decided.
function readConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeConsent(value) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch (e) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("[CookieConsent] Failed to save consent:", e);
    }
  }
}

// Notify gtag of the consent state so GA respects it. Works alongside a
// future Google FundingChoices CMP without conflicting.
function updateGtagConsent(granted) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  window.gtag("consent", "update", {
    ad_storage: granted ? "granted" : "denied",
    analytics_storage: granted ? "granted" : "denied",
    ad_user_data: granted ? "granted" : "denied",
    ad_personalization: granted ? "granted" : "denied",
  });
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Default to denied for EU/CCPA compliance until the user picks.
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("consent", "default", {
        ad_storage: "denied",
        analytics_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
      });
    }

    const current = readConsent();
    if (current == null) {
      // Show banner after a short delay to avoid layout jump
      const t = setTimeout(() => setVisible(true), 400);
      return () => clearTimeout(t);
    }
    // Re-apply saved decision on load
    updateGtagConsent(current.granted === true);
  }, []);

  const accept = () => {
    const value = { granted: true, at: new Date().toISOString() };
    writeConsent(value);
    updateGtagConsent(true);
    setVisible(false);
  };

  const reject = () => {
    const value = { granted: false, at: new Date().toISOString() };
    writeConsent(value);
    updateGtagConsent(false);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-consent"
      role="dialog"
      aria-labelledby="cookie-consent-heading"
      className="fixed inset-x-0 bottom-0 z-50 border-t-2 border-foreground bg-background/95 backdrop-blur-md shadow-2xl"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 grid gap-4 md:grid-cols-[auto_1fr_auto] md:items-start">
        <div className="inline-flex items-center gap-3 md:mt-1">
          <Cookie size={22} weight="fill" className="text-primary" />
          <span
            id="cookie-consent-heading"
            className="font-display font-bold text-lg md:hidden"
          >
            Cookies & ads
          </span>
        </div>

        <div className="min-w-0">
          <div className="hidden md:block font-display font-bold text-lg mb-1">
            Cookies & ads
          </div>
          <p className="text-sm text-foreground/80 leading-relaxed">
            We use cookies for essential site functionality, anonymous analytics,
            and personalized advertising (Google AdSense). Rejecting will stop
            personalization but keep the site working.
            {" "}
            <Link to="/privacy" className="underline underline-offset-2 hover:text-primary">
              Read our privacy policy
            </Link>.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 md:flex-nowrap md:items-center md:justify-end">
          <button
            onClick={reject}
            data-testid="cookie-reject"
            className="h-10 px-4 border border-foreground/25 text-sm hover:bg-muted transition-colors duration-200 inline-flex items-center gap-1"
          >
            Reject non-essential
          </button>
          <button
            onClick={accept}
            data-testid="cookie-accept"
            className="h-10 px-5 bg-foreground text-background text-sm font-semibold hover:bg-primary transition-colors duration-200"
          >
            Accept all
          </button>
          <button
            onClick={reject}
            data-testid="cookie-close"
            aria-label="Close"
            className="hidden md:inline-flex items-center justify-center w-10 h-10 border border-foreground/15 text-foreground/60 hover:text-foreground transition-colors duration-200"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
